import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Paper,
  Grid2,
} from "@mui/material";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import CustomTypography from "../../components/common/form/CustomTypography";
import StoreTable from "../../components/swagstore/StoreTable";
import CustomTextField from "../../components/common/textFields/TextField";

const StoreListing: React.FC = () => {
  const { swagStoreStore, userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialPage = searchParams.get("page_no")
    ? parseInt(searchParams.get("page_no") as string, 10)
    : 1;
  const initialLimit = searchParams.get("limit")
    ? parseInt(searchParams.get("limit") as string, 10)
    : 30;
  const initialSearchText = searchParams.get("search_text") || "";

  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearchText);
  const [previousSearchTerm, setPreviousSearchTerm] =
    useState(initialSearchText);

  const canViewBalance = userStore.hasPermission(EmployeeRoles.VIEW_SWAGSTORE);
  const canAddBalance = userStore.hasPermission(
    EmployeeRoles.ADD_WALLET_BALANCE,
  );
  const canViewPurchaseTransactions = userStore.hasPermission(
    EmployeeRoles.VIEW_SWAGSTORE,
  );

  const loadStores = useCallback(
    (page: number, limit: number, searchText: string) => {
      setIsLoading(true);
      swagStoreStore.setStoreListingSearchText(searchText);
      return swagStoreStore
        .getAllStores(page, limit, searchText)
        .catch((err) =>
          showError(
            "Error",
            err?.response?.data?.message ||
              "Something went wrong! Please try again later.",
          ),
        )
        .finally(() => {
          setIsLoading(false);
        });
    },
    [swagStoreStore, showError],
  );

  useEffect(() => {
    loadStores(initialPage, initialLimit, initialSearchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const updateQueryParams = () => {
      const params = new URLSearchParams();
      params.set("page_no", swagStoreStore.storeListingPage.toString());
      params.set("limit", swagStoreStore.storeListingLimit.toString());
      if (swagStoreStore.storeListingSearchText) {
        params.set("search_text", swagStoreStore.storeListingSearchText);
      }
      navigate({ search: params.toString() }, { replace: true });
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    swagStoreStore.storeListingPage,
    swagStoreStore.storeListingLimit,
    swagStoreStore.storeListingSearchText,
    navigate,
  ]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    swagStoreStore.setStoreListingPage(newPage);
    loadStores(
      newPage,
      swagStoreStore.storeListingLimit,
      swagStoreStore.storeListingSearchText,
    );
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    swagStoreStore.setStoreListingLimit(newLimit);
    loadStores(1, newLimit, swagStoreStore.storeListingSearchText);
  };

  const handleSearch = (query: string) => {
    setSearchTerm(query);

    // If search becomes empty and it previously had text, automatically call API
    const trimmedQuery = query.trim();
    const trimmedPrevious = previousSearchTerm.trim();

    if (trimmedQuery.length === 0 && trimmedPrevious.length > 0) {
      setSearchTerm("");
      swagStoreStore.setStoreListingPage(1);
      loadStores(1, swagStoreStore.storeListingLimit, "");
    }

    setPreviousSearchTerm(query);
  };

  const handleStatusChange = (storeId: number | string, isActive: boolean) => {
    swagStoreStore
      .updateStoreStatus(storeId, isActive)
      .then((res) => {
        showSuccess("Store status updated successfully");
      })
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        ),
      );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      const trimmedSearchTerm = searchTerm.trim();
      setSearchTerm(trimmedSearchTerm);
      swagStoreStore.setStoreListingPage(1);
      loadStores(1, swagStoreStore.storeListingLimit, trimmedSearchTerm);
    }
  };

  const handleAddBalanceSuccess = (balance: number) => {
    showSuccess(
      `Balance added successfully. New wallet balance: ₹${balance.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`,
    );
  };

  const handlePurchaseTransactions = (store: {
    store_id?: number | string;
    id?: number | string;
    customer_id?: number;
    store_name?: string;
  }) => {
    const storeId = store.id;
    if (storeId == null) return;

    const params = new URLSearchParams();
    params.set("store_id", String(storeId));
    if (store.customer_id) {
      params.set("customer_id", String(store.customer_id));
    }
    if (store.store_name) {
      params.set("store_name", store.store_name);
    }
    navigate(`/swagstore/purchase-transactions?${params.toString()}`);
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {isLoading ? (
        <Container
          sx={{
            minHeight: "80vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Container>
      ) : (
        <Paper
          sx={{
            padding: 5,
            paddingBottom: 3,
            paddingTop: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <CustomTypography variant="heading3" fontWeight={700}>
              Store Listing
            </CustomTypography>
          </Box>

          <Box
            sx={{
              mt: 3,
              mb: 3,
              display: "flex",
              justifyContent: "space-between",
              gap: 4,
            }}
          >
            <CustomTextField
              placeholder="Search by store name"
              value={searchTerm}
              onSearch={(query) => handleSearch(query)}
              onKeyDown={handleKeyDown}
              width={"100%"}
            />
            {/* <Box sx={{ position: "relative", display: "inline-block" }}>
              <AppButton
                variant="grayscale"
                label="Filter"
                startIcon={<FilterAltOutlinedIcon />}
                onClick={() => {
                }}
                customStyles={{ width: "130px" }}
              />
            </Box> */}
          </Box>

          {swagStoreStore.stores.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="10rem"
              textAlign="center"
            >
              <Typography variant="h6" fontWeight="500" color="text.secondary">
                No stores found
              </Typography>
            </Box>
          )}

          {swagStoreStore.stores.length > 0 && (
            <Box>
              <StoreTable
                maxHeight={window.innerHeight - 350}
                stores={swagStoreStore.stores}
                limit={swagStoreStore.storeListingLimit}
                page_no={swagStoreStore.storeListingPage}
                total_count={swagStoreStore.storeListingTotalCount}
                total_pages={swagStoreStore.storeListingTotalPages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onStatusChange={handleStatusChange}
                canUpdateStatus={userStore.hasPermission(
                  EmployeeRoles.ADD_EDIT_PRODUCTS_IN_SWAGSTORE,
                )}
                canAddBalance={canAddBalance}
                canViewBalance={canViewBalance}
                canViewPurchaseTransactions={canViewPurchaseTransactions}
                onAddBalanceSuccess={handleAddBalanceSuccess}
                onPurchaseTransactions={handlePurchaseTransactions}
              />
            </Box>
          )}
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(StoreListing);
